import type { ReactNode } from "react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Pane } from "../FieldDiffRow";
import type { DiffGroup, DiffGroupKind } from "./diffGroups";
import type { TurnFieldProposal } from "../../../util/slopifyThread";

// ---------------------------------------------------------------------------
// value extraction helpers — read a member field's BEFORE/AFTER value
// ---------------------------------------------------------------------------

type Side = "before" | "after";

const sideValue = (members: TurnFieldProposal[], name: string, side: Side): unknown => {
  const m = members.find((f) => f.fieldName === name);
  if (!m) return undefined;
  return side === "before" ? m.before : m.after;
};

const asArray = (v: unknown): any[] => (Array.isArray(v) ? v : []);
const asStr = (v: unknown): string => (v === null || v === undefined ? "" : String(v));

// ---------------------------------------------------------------------------
// row models
// ---------------------------------------------------------------------------

interface AnswerRow {
  text: string;
  correct: boolean;
  /** undefined ⇒ this type has no image concept (quizz-3) */
  hasImage?: boolean;
}

interface QuestionRow {
  text: string;
  answers: AnswerRow[];
  winText: string;
  loseText: string;
  imageAnswers: boolean;
  allowMultiple: boolean;
}

const quizz3Rows = (members: TurnFieldProposal[], side: Side): AnswerRow[] => {
  const texts = asArray(sideValue(members, "list_answers", side));
  const corrects = asArray(sideValue(members, "correct_answers", side));
  return texts.map((tx, i) => ({ text: asStr(tx), correct: Boolean(corrects[i]) }));
};

const imageRows = (members: TurnFieldProposal[], side: Side): AnswerRow[] =>
  asArray(sideValue(members, "list_answers", side)).map((it) => ({
    text: asStr(it?.text),
    correct: Boolean(it?.is_right),
    hasImage: Number(it?.imageId) > 0,
  }));

const sortRows = (members: TurnFieldProposal[], side: Side): string[] =>
  [...asArray(sideValue(members, "answers", side))]
    .sort((a, b) => (Number(a?.index) || 0) - (Number(b?.index) || 0))
    .map((it) => asStr(it?.text));

const questionRows = (members: TurnFieldProposal[], side: Side): QuestionRow[] =>
  asArray(sideValue(members, "questions", side)).map((q) => ({
    text: asStr(q?.text),
    answers: asArray(q?.answers).map((a) => ({
      text: asStr(a?.text),
      correct: Boolean(a?.is_right),
      hasImage: Number(a?.imageId) > 0,
    })),
    winText: asStr(q?.winText),
    loseText: asStr(q?.loseText),
    imageAnswers: Boolean(q?.imageAnswers),
    allowMultiple: Boolean(q?.allowMultiple),
  }));

const sameAnswer = (a?: AnswerRow, b?: AnswerRow): boolean =>
  !!a && !!b && a.text === b.text && a.correct === b.correct && a.hasImage === b.hasImage;

// ---------------------------------------------------------------------------
// presentational primitives
// ---------------------------------------------------------------------------

const Marker = ({ correct }: { correct: boolean }) => (
  <span className={`shrink-0 ${correct ? "text-green-600" : "text-gray-400"}`} aria-hidden="true">
    {correct ? "✓" : "○"}
  </span>
);

const Chip = ({ children }: { children: ReactNode }) => (
  <span className="shrink-0 text-[9px] px-1 rounded bg-gray-100 text-gray-500">{children}</span>
);

const ImageBadge = ({ hasImage }: { hasImage: boolean }) => {
  const { t } = useTranslation();
  return (
    <span
      className={`shrink-0 text-[9px] px-1 rounded ${
        hasImage ? "bg-green-100 text-green-700" : "bg-gray-100 text-gray-500"
      }`}
    >
      {hasImage
        ? t("general.assistantScenarIa.diffPreview.imageSet")
        : t("general.assistantScenarIa.diffPreview.imageMissing")}
    </span>
  );
};

/** Left-accent + background applied to a row/block whose content changed. */
const changedClass = (variant: Side, changed: boolean): string => {
  if (!changed) return "border-l-2 border-transparent";
  return variant === "before"
    ? "border-l-2 border-gray-300 bg-white"
    : "border-l-2 border-green-400 bg-green-100";
};

const EmptyHint = () => {
  const { t } = useTranslation();
  return (
    <span className="italic text-gray-400">{t("general.assistantScenarIa.diffPreview.empty")}</span>
  );
};

// ---------------------------------------------------------------------------
// per-kind lists
// ---------------------------------------------------------------------------

const AnswerList = ({
  rows,
  otherRows,
  variant,
}: {
  rows: AnswerRow[];
  otherRows: AnswerRow[];
  variant: Side;
}) => {
  if (rows.length === 0) return <EmptyHint />;
  return (
    <ul className="m-0 p-0 list-none space-y-0.5">
      {rows.map((r, i) => (
        <li
          key={i}
          className={`flex items-start gap-1 rounded pr-0.5 ${changedClass(
            variant,
            !sameAnswer(r, otherRows[i]),
          )}`}
        >
          <Marker correct={r.correct} />
          <span className="flex-1 break-words">{r.text || "—"}</span>
          {r.hasImage !== undefined && <ImageBadge hasImage={r.hasImage} />}
        </li>
      ))}
    </ul>
  );
};

const SortList = ({
  rows,
  otherRows,
  variant,
}: {
  rows: string[];
  otherRows: string[];
  variant: Side;
}) => {
  if (rows.length === 0) return <EmptyHint />;
  return (
    <ol className="m-0 p-0 list-none space-y-0.5">
      {rows.map((text, i) => (
        <li
          key={i}
          className={`flex items-start gap-1 rounded pr-0.5 ${changedClass(
            variant,
            otherRows[i] !== text,
          )}`}
        >
          <span className="shrink-0 text-gray-400 tabular-nums">{i + 1}.</span>
          <span className="flex-1 break-words">{text || "—"}</span>
        </li>
      ))}
    </ol>
  );
};

const QuestionsList = ({
  rows,
  otherRows,
  variant,
}: {
  rows: QuestionRow[];
  otherRows: QuestionRow[];
  variant: Side;
}) => {
  const { t } = useTranslation();
  if (rows.length === 0) return <EmptyHint />;
  return (
    <div className="space-y-1.5">
      {rows.map((q, i) => {
        const changed = JSON.stringify(q) !== JSON.stringify(otherRows[i]);
        return (
          <div key={i} className={`rounded p-1 ${changedClass(variant, changed)}`}>
            <div className="flex items-center gap-1 flex-wrap mb-0.5">
              <span className="font-medium text-gray-700">
                {t("general.assistantScenarIa.diffPreview.questionLabel", { n: i + 1 })}
              </span>
              <Chip>
                {q.allowMultiple
                  ? t("general.assistantScenarIa.diffPreview.multiple")
                  : t("general.assistantScenarIa.diffPreview.single")}
              </Chip>
              {q.imageAnswers && (
                <Chip>{t("general.assistantScenarIa.diffPreview.imageAnswers")}</Chip>
              )}
            </div>
            <div className="break-words mb-0.5">{q.text || "—"}</div>
            <ul className="m-0 p-0 list-none space-y-0.5 pl-2">
              {q.answers.map((a, j) => (
                <li key={j} className="flex items-start gap-1">
                  <Marker correct={a.correct} />
                  <span className="flex-1 break-words">{a.text || "—"}</span>
                  {q.imageAnswers && a.hasImage !== undefined && (
                    <ImageBadge hasImage={a.hasImage} />
                  )}
                </li>
              ))}
            </ul>
            {(q.winText || q.loseText) && (
              <div className="text-[9px] text-gray-500 mt-0.5 pl-2 space-y-0.5">
                {q.winText && (
                  <div className="break-words">
                    {t("general.assistantScenarIa.diffPreview.win")}: {q.winText}
                  </div>
                )}
                {q.loseText && (
                  <div className="break-words">
                    {t("general.assistantScenarIa.diffPreview.lose")}: {q.loseText}
                  </div>
                )}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

// ---------------------------------------------------------------------------
// two-pane wrapper + dispatcher
// ---------------------------------------------------------------------------

const TwoPane = ({ before, after }: { before: ReactNode; after: ReactNode }) => {
  const { t } = useTranslation();
  // One shared expand state — toggling either side unwraps/wraps BOTH panes, so
  // a structured before/after stays aligned (no half-expanded "Questions" card).
  const [expanded, setExpanded] = useState(false);
  const toggle = () => setExpanded((v) => !v);
  return (
    <div className="grid grid-cols-2 gap-2">
      <Pane
        label={t("general.assistantScenarIa.diffBefore") as string}
        variant="before"
        expanded={expanded}
        onToggle={toggle}
      >
        {before}
      </Pane>
      <Pane
        label={t("general.assistantScenarIa.diffAfter") as string}
        variant="after"
        expanded={expanded}
        onToggle={toggle}
      >
        {after}
      </Pane>
    </div>
  );
};

const renderBody = (kind: DiffGroupKind, members: TurnFieldProposal[]): ReactNode => {
  if (kind === "quizz3Answers" || kind === "imageAnswers") {
    const build = kind === "quizz3Answers" ? quizz3Rows : imageRows;
    const before = build(members, "before");
    const after = build(members, "after");
    return (
      <TwoPane
        before={<AnswerList rows={before} otherRows={after} variant="before" />}
        after={<AnswerList rows={after} otherRows={before} variant="after" />}
      />
    );
  }
  if (kind === "sortAnswers") {
    const before = sortRows(members, "before");
    const after = sortRows(members, "after");
    return (
      <TwoPane
        before={<SortList rows={before} otherRows={after} variant="before" />}
        after={<SortList rows={after} otherRows={before} variant="after" />}
      />
    );
  }
  if (kind === "randomQuestions") {
    const before = questionRows(members, "before");
    const after = questionRows(members, "after");
    return (
      <TwoPane
        before={<QuestionsList rows={before} otherRows={after} variant="before" />}
        after={<QuestionsList rows={after} otherRows={before} variant="after" />}
      />
    );
  }
  return null;
};

/**
 * After-only structured preview (no panes / no change-highlight) for the read-only
 * history accordion, where only the applied value is shown.
 */
export const StructuredAfterPreview = ({ group }: { group: DiffGroup }) => {
  const { kind, members } = group;
  if (kind === "quizz3Answers" || kind === "imageAnswers") {
    const rows = (kind === "quizz3Answers" ? quizz3Rows : imageRows)(members, "after");
    return <AnswerList rows={rows} otherRows={rows} variant="after" />;
  }
  if (kind === "sortAnswers") {
    const rows = sortRows(members, "after");
    return <SortList rows={rows} otherRows={rows} variant="after" />;
  }
  if (kind === "randomQuestions") {
    const rows = questionRows(members, "after");
    return <QuestionsList rows={rows} otherRows={rows} variant="after" />;
  }
  return null;
};

/**
 * A structured BEFORE/AFTER card for a fused quizz array group, with the same
 * outer chrome (checkbox, label, unchanged/manually-dirtied badges) as
 * FieldDiffRow so the two render styles sit consistently in the proposal list.
 */
export const StructuredGroupCard = ({
  group,
  checked,
  onToggle,
  manuallyDirtied,
}: {
  group: DiffGroup;
  checked: boolean;
  onToggle: (next: boolean) => void;
  manuallyDirtied: boolean;
}) => {
  const { t } = useTranslation();
  const baseClass = group.unchanged
    ? "opacity-60 bg-gray-50 border-gray-100"
    : "bg-white border-gray-200";
  return (
    <div className={`rounded-md border p-2 mb-1.5 ${baseClass}`}>
      <div className="flex items-center gap-2 mb-1">
        <input
          type="checkbox"
          className="h-3.5 w-3.5 accent-green-500 disabled:opacity-50"
          checked={checked}
          disabled={group.unchanged}
          onChange={(e) => onToggle(e.target.checked)}
          aria-label={group.label}
        />
        <span className="text-[11px] font-medium text-gray-700 truncate flex-1" title={group.label}>
          {group.label}
        </span>
        {group.unchanged && (
          <span className="text-[10px] italic text-gray-400">
            {t("general.assistantScenarIa.unchanged")}
          </span>
        )}
        {manuallyDirtied && !group.unchanged && (
          <span
            className="text-[10px] text-amber-600 whitespace-nowrap"
            title={t("general.assistantScenarIa.manuallyDirtiedTooltip") as string}
          >
            {t("general.assistantScenarIa.manuallyDirtiedBadge")}
          </span>
        )}
      </div>
      {renderBody(group.kind, group.members)}
    </div>
  );
};

export default StructuredGroupCard;
